import { useEffect, type RefObject } from 'react';
import { helpForAction } from '@/help/copy';
import i18n, { toEnglish } from '@/i18n';

/**
 * Gives every button a hover explanation, from its own label.
 *
 * Done by observation rather than by wrapping each call site: there are over
 * two hundred buttons, most with inline handlers, and a button added later
 * would not inherit anything from a codemod. Setting the native `title` is also
 * an attribute React does not manage, so this cannot fight a re-render — the
 * worst case is a title that is reapplied.
 *
 * Buttons whose label has no entry are left alone. A button that already
 * carries a `title`, or sits inside a MUI Tooltip, is left alone too so a
 * deliberate one is never overwritten.
 *
 * Cost: a grid re-render mutates the DOM hundreds of times a second, and a
 * full-document pass per mutation was measurable while scrolling. So the
 * observer watches the page area (`<main>`) and only the top level of <body>
 * — where dialogs and menus are portalled — and coalesces a burst of
 * mutations into one pass per animation frame.
 */
export function useActionTooltips(scope: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const main = scope.current;
    if (!main) return;

    const label = (root: ParentNode) => {
      for (const button of root.querySelectorAll<HTMLElement>('button')) {
        if (button.title || button.getAttribute('aria-describedby')) continue;
        // Icon-only buttons carry their meaning in aria-label, not text.
        const text = (button.textContent ?? '').trim();
        if (!text) continue;
        const help = helpForAction(toEnglish(text));
        if (help) button.title = i18n.t(help);
      }
    };

    // Portals (dialogs, menus) are direct children of <body>, outside <main>.
    const portals = () =>
      Array.from(document.body.children).filter(
        (el): el is HTMLElement => el instanceof HTMLElement && el !== main && !el.contains(main),
      );

    let frame = 0;
    let wholePortals = false;
    const schedule = (portalChange: boolean) => {
      wholePortals ||= portalChange;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        label(main);
        if (wholePortals) for (const portal of portals()) label(portal);
        wholePortals = false;
      });
    };

    label(main);
    for (const portal of portals()) label(portal);

    const inMain = new MutationObserver(() => schedule(false));
    inMain.observe(main, { childList: true, subtree: true });
    // A dialog's buttons arrive with it, or shortly after as its data loads;
    // watch each portal subtree too, but only the ones that exist.
    const inPortals = new MutationObserver(() => schedule(true));
    const watchPortals = () => {
      inPortals.disconnect();
      for (const portal of portals()) inPortals.observe(portal, { childList: true, subtree: true });
    };
    watchPortals();
    const topLevel = new MutationObserver(() => {
      watchPortals();
      schedule(true);
    });
    topLevel.observe(document.body, { childList: true });

    // Titles are unmanaged attributes, so a language switch has to re-run the pass.
    const relabel = () => {
      for (const root of [main, ...portals()]) {
        for (const button of root.querySelectorAll<HTMLElement>('button')) button.title = '';
      }
      schedule(true);
    };
    i18n.on('languageChanged', relabel);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      inMain.disconnect();
      inPortals.disconnect();
      topLevel.disconnect();
      i18n.off('languageChanged', relabel);
    };
  }, [scope]);
}
